import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import {
  allOutputs,
  fileName,
  findBoxes,
  normAngle,
  output,
  sameCard,
  sortBoxes,
  toBlob,
  toManual,
} from '../../utils/card-cropper/crop.js';

let seq = 0;
const nextId = () => {
  seq += 1;
  return seq;
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read image'));
    img.src = src;
  });
}

function isImageFile(file) {
  return file.type.startsWith('image/');
}

function cropFrom(settings) {
  return {
    sens: Number(settings.sens),
    pad: Number(settings.pad) / 100,
    prefix: (settings.prefix.trim() || 'card').replace(/[^\w-]+/g, '_'),
    fmt: settings.fmt,
    ebay: settings.ebay,
    bg: settings.bg || '#ffffff',
  };
}

function deliver(name, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default function useCardCropper() {
  const [scans, setScans] = useState([]);
  const [settings, setSettings] = useState({
    sens: 50,
    pad: 1,
    prefix: 'card',
    fmt: 'jpg',
    ebay: false,
    bg: '#ffffff',
  });
  const [selId, setSelId] = useState(null);
  const [status, setStatus] = useState({ msg: '', cls: '' });
  const [viewerIndex, setViewerIndex] = useState(null);
  const [draft, setDraft] = useState(null);
  const [zipBusy, setZipBusy] = useState(false);
  const [gridRev, setGridRev] = useState(0);

  const scansRef = useRef(scans);
  scansRef.current = scans;
  const selRef = useRef(null);
  const viewerRef = useRef(null);
  viewerRef.current = viewerIndex;
  const crop = useMemo(() => cropFrom(settings), [settings]);
  const cropRef = useRef(crop);
  cropRef.current = crop;
  const gridTimer = useRef(null);
  const sensTimer = useRef(null);
  const bgTimer = useRef(null);
  const addingRef = useRef(false);

  const publish = useCallback((next, { grid = false } = {}) => {
    scansRef.current = next;
    setScans(next);
    if (grid) setGridRev((n) => n + 1);
  }, []);

  const refresh = useCallback((opts) => {
    publish(scansRef.current.slice(), opts);
  }, [publish]);

  const gridSoon = useCallback(() => {
    clearTimeout(gridTimer.current);
    gridTimer.current = setTimeout(() => setGridRev((n) => n + 1), 120);
  }, []);

  const clearSelection = useCallback(() => {
    selRef.current = null;
    setSelId(null);
  }, []);

  const selectBox = useCallback((scan, box) => {
    selRef.current = box ? { scan, box } : null;
    setSelId(box ? box.id : null);
  }, []);

  const detectScan = useCallback((scan, sens) => {
    const keep = scan.boxes.filter((b) => b.manual);
    const fresh = findBoxes(scan, sens)
      .filter((d) => !keep.some((k) => sameCard(d, k)))
      .map((d) => ({ ...d, id: nextId() }));
    scan.boxes = fresh.concat(keep);
    sortBoxes(scan);
    const sel = selRef.current;
    if (sel && sel.scan === scan && !scan.boxes.includes(sel.box)) {
      selRef.current = null;
      setSelId(null);
    }
  }, []);

  const addFiles = useCallback(async (fileList) => {
    const imgs = [...fileList].filter(isImageFile);
    if (!imgs.length) {
      if (fileList?.length) setStatus({ msg: 'Those files are not images.', cls: 'err' });
      return;
    }
    if (addingRef.current) return;
    addingRef.current = true;
    let failed = false;
    try {
      for (const file of imgs) {
        setStatus({ msg: `Finding cards in ${file.name}…`, cls: '' });
        await new Promise((resolve) => { setTimeout(resolve, 0); });
        const url = URL.createObjectURL(file);
        try {
          const img = await loadImage(url);
          const scan = { id: nextId(), img, url, name: file.name, boxes: [] };
          detectScan(scan, cropRef.current.sens);
          publish(scansRef.current.concat(scan), { grid: true });
        } catch {
          failed = true;
          URL.revokeObjectURL(url);
          setStatus({ msg: `Couldn't read ${file.name}.`, cls: 'err' });
        }
      }
      if (!failed) setStatus({ msg: '', cls: '' });
    } finally {
      addingRef.current = false;
    }
  }, [detectScan, publish]);

  const clearAll = useCallback(() => {
    scansRef.current.forEach((scan) => URL.revokeObjectURL(scan.url));
    clearSelection();
    setDraft(null);
    setViewerIndex(null);
    setStatus({ msg: '', cls: '' });
    publish([], { grid: true });
  }, [clearSelection, publish]);

  const detectMissing = useCallback((scan) => {
    const fresh = findBoxes(scan, cropRef.current.sens)
      .filter((d) => !scan.boxes.some((b) => sameCard(d, b)))
      .map((d) => ({ ...d, id: nextId() }));
    scan.boxes.push(...fresh);
    sortBoxes(scan);
    refresh({ grid: true });
    const n = fresh.length;
    setStatus({
      msg: n
        ? `Added ${n} box${n === 1 ? '' : 'es'} to ${scan.name}.`
        : `Every detected card on ${scan.name} already has a box.`,
      cls: n ? 'ok' : '',
    });
  }, [refresh]);

  const clearBoxes = useCallback((scan) => {
    scan.boxes = [];
    if (selRef.current?.scan === scan) clearSelection();
    refresh({ grid: true });
  }, [clearSelection, refresh]);

  const removeScan = useCallback((scan) => {
    URL.revokeObjectURL(scan.url);
    if (selRef.current?.scan === scan) clearSelection();
    publish(scansRef.current.filter((s) => s !== scan), { grid: true });
  }, [clearSelection, publish]);

  const removeBox = useCallback((scan, box) => {
    scan.boxes = scan.boxes.filter((item) => item !== box);
    if (selRef.current?.box === box) clearSelection();
    refresh({ grid: true });
  }, [clearSelection, refresh]);

  const toggleSkip = useCallback((box) => {
    box.on = !box.on;
    refresh({ grid: true });
  }, [refresh]);

  const setTilt = useCallback((box, degrees) => {
    const d = Math.max(-45, Math.min(45, degrees));
    toManual(box);
    box.angle = d * Math.PI / 180;
    refresh();
    gridSoon();
  }, [gridSoon, refresh]);

  const rotatePhoto = useCallback((box, turns) => {
    box.rot = (box.rot || 0) + turns;
    setGridRev((n) => n + 1);
  }, []);

  const createBox = useCallback((scan, partial) => {
    const box = { ...partial, id: nextId() };
    scan.boxes.push(box);
    sortBoxes(scan);
    selRef.current = { scan, box };
    setSelId(box.id);
    setDraft(null);
    refresh({ grid: true });
  }, [refresh]);

  const onLive = useCallback(() => {
    refresh();
  }, [refresh]);

  const onCommit = useCallback(() => {
    refresh({ grid: true });
  }, [refresh]);

  const setSens = useCallback((value) => {
    const sens = Number(value);
    setSettings((prev) => ({ ...prev, sens }));
    clearTimeout(sensTimer.current);
    sensTimer.current = setTimeout(() => {
      scansRef.current.forEach((scan) => detectScan(scan, sens));
      refresh({ grid: true });
    }, 150);
  }, [detectScan, refresh]);

  const setPad = useCallback((value) => {
    setSettings((prev) => ({ ...prev, pad: Number(value) }));
    gridSoon();
  }, [gridSoon]);

  const setPrefix = useCallback((value) => {
    setSettings((prev) => ({ ...prev, prefix: value }));
  }, []);

  const setFmt = useCallback((value) => {
    setSettings((prev) => ({ ...prev, fmt: value }));
  }, []);

  const setEbay = useCallback((ebay) => {
    setSettings((prev) => ({ ...prev, ebay }));
    setGridRev((n) => n + 1);
  }, []);

  const setBg = useCallback((bg) => {
    setSettings((prev) => ({ ...prev, bg }));
    clearTimeout(bgTimer.current);
    bgTimer.current = setTimeout(() => setGridRev((n) => n + 1), 60);
  }, []);

  const openViewer = useCallback((n) => {
    const outs = allOutputs(scansRef.current);
    if (!outs.length) return;
    setViewerIndex(Math.min(Math.max(1, n), outs.length));
  }, []);

  const closeViewer = useCallback(() => setViewerIndex(null), []);

  const stepViewer = useCallback((delta) => {
    setViewerIndex((current) => {
      if (current == null) return current;
      const outs = allOutputs(scansRef.current);
      if (!outs.length) return null;
      return Math.min(Math.max(1, current + delta), outs.length);
    });
  }, []);

  const saveOutput = useCallback(async (item) => {
    if (!item) return;
    const st = cropRef.current;
    const blob = await toBlob(output(item.box, item.scan.img, st), st.fmt);
    const name = fileName(item.n, st);
    if (!blob) {
      setStatus({ msg: `Couldn't save ${name}.`, cls: 'err' });
      return;
    }
    deliver(name, blob);
    setStatus({ msg: `Downloaded ${name}`, cls: 'ok' });
  }, []);

  const downloadAll = useCallback(async () => {
    const st = cropRef.current;
    const outs = allOutputs(scansRef.current);
    if (!outs.length || zipBusy) return;
    setZipBusy(true);
    setStatus({ msg: `Building ${outs.length} full-size photos…`, cls: '' });
    await new Promise((resolve) => { setTimeout(resolve, 0); });
    try {
      const zip = new JSZip();
      for (const item of outs) {
        const blob = await toBlob(output(item.box, item.scan.img, st), st.fmt);
        if (!blob) throw new Error('empty blob');
        zip.file(fileName(item.n, st), blob);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const name = `${st.prefix}_photos.zip`;
      deliver(name, blob);
      setStatus({ msg: `Downloaded ${name}`, cls: 'ok' });
    } catch {
      setStatus({ msg: 'Couldn\'t build the zip. Save cards one at a time instead.', cls: 'err' });
    } finally {
      setZipBusy(false);
    }
  }, [zipBusy]);

  const addFilesRef = useRef(addFiles);
  addFilesRef.current = addFiles;

  const onKeyRef = useRef(null);
  onKeyRef.current = (e) => {
    if (viewerRef.current != null) {
      if (e.key === 'Escape') closeViewer();
      else if (e.key === 'ArrowLeft') stepViewer(-1);
      else if (e.key === 'ArrowRight') stepViewer(1);
      else return;
      e.preventDefault();
      return;
    }
    const sel = selRef.current;
    if (!sel) return;
    const tag = (e.target.tagName || '').toLowerCase();
    if ((tag === 'input' && e.target.type !== 'range') || tag === 'select' || tag === 'textarea') return;
    const { scan, box } = sel;
    const step = scan.img.naturalWidth / (e.shiftKey ? 100 : 400);
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      removeBox(scan, box);
      return;
    }
    if (e.key === 'Escape') {
      selectBox(null, null);
      return;
    }
    const moves = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    if (tag !== 'input' && moves[e.key]) {
      e.preventDefault();
      toManual(box);
      box.cx += moves[e.key][0];
      box.cy += moves[e.key][1];
    } else if (e.key === '[' || e.key === ']') {
      toManual(box);
      box.angle += (e.key === ']' ? 1 : -1) * (e.shiftKey ? 2 : 0.5) * Math.PI / 180;
      normAngle(box);
    } else {
      return;
    }
    refresh();
    gridSoon();
  };

  useEffect(() => {
    const onKey = (e) => onKeyRef.current(e);
    const onPaste = (e) => {
      const files = [...(e.clipboardData?.files || [])];
      if (files.length) addFilesRef.current(files);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('paste', onPaste);
    const previousTitle = document.title;
    document.title = 'Card Cropper · Pull Vault';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('paste', onPaste);
      document.title = previousTitle;
      clearTimeout(gridTimer.current);
      clearTimeout(sensTimer.current);
      clearTimeout(bgTimer.current);
      scansRef.current.forEach((scan) => URL.revokeObjectURL(scan.url));
    };
  }, []);

  useEffect(() => {
    if (viewerIndex == null) return;
    const count = allOutputs(scans).length;
    if (viewerIndex > count) setViewerIndex(count || null);
  }, [viewerIndex, scans]);

  useEffect(() => {
    if (viewerIndex == null) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [viewerIndex]);

  const outputs = useMemo(() => allOutputs(scans), [scans]);
  const selection = selId != null ? selRef.current : null;
  const viewerItem = viewerIndex != null ? outputs[viewerIndex - 1] || null : null;

  return {
    scans,
    settings,
    crop,
    selection,
    status,
    draft,
    setDraft,
    zipBusy,
    gridRev,
    outputs,
    viewerIndex,
    viewerItem,
    addFiles,
    clearAll,
    detectMissing,
    clearBoxes,
    removeScan,
    removeBox,
    toggleSkip,
    setTilt,
    rotatePhoto,
    createBox,
    selectBox,
    onLive,
    onCommit,
    setSens,
    setPad,
    setPrefix,
    setFmt,
    setEbay,
    setBg,
    openViewer,
    closeViewer,
    stepViewer,
    saveOutput,
    downloadAll,
  };
}
